import type { FormManagementExportFormatId } from '@/contracts/form-management'

export type AnalyticsExportFormat = Extract<FormManagementExportFormatId, 'csv' | 'excel' | 'json'>

export type AnalyticsExportFile = {
  format: AnalyticsExportFormat
  fileName: string
  mimeType: string
  content: string
  rowCount: number
  mockSafe: true
}

export type AnalyticsExportColumn = {
  id: keyof AnalyticsExportRow
  label: string
}

export type AnalyticsExportRow = {
  questionId: string
  questionTitle: string
  questionType: string
  chartKind: string
  segmentLabel: string
  segmentValue: number
  segmentPercent: number
  totalAnswers: number
  skipped: number
  invalid: number
  completionRate: number
  suppressed: boolean
  suppressionReason: string
  samplePolicy: AnalyticsExportSamplePolicy
}

export type AnalyticsExportSamplePolicy =
  | 'aggregate_only'
  | 'pii_format_status_only'
  | 'samples_not_exported'

export type AnalyticsExportChartInput = {
  questionId: string
  questionTitle: string
  questionType: string
  chartKind: string
  totalAnswers: number
  skipped: number
  invalid?: number
  completionRate: number
  segments: Array<{
    label: string
    value: number
    percent: number
    suppressed?: boolean
  }>
  samples?: Array<{
    value: string
  }>
  suppression?: {
    suppressed: boolean
    reason?: 'small_sample'
    threshold: number
    sampleSize: number
  }
}

type BuildAnalyticsExportFileInput = {
  format: AnalyticsExportFormat
  formTitle: string
  questionCharts: AnalyticsExportChartInput[]
  generatedAt?: Date
}

const analyticsExportColumns: AnalyticsExportColumn[] = [
  { id: 'questionId', label: '題目 ID' },
  { id: 'questionTitle', label: '題目標題' },
  { id: 'questionType', label: '題型' },
  { id: 'chartKind', label: '圖表型態' },
  { id: 'segmentLabel', label: '統計項目' },
  { id: 'segmentValue', label: '數量' },
  { id: 'segmentPercent', label: '百分比' },
  { id: 'totalAnswers', label: '有效回答數' },
  { id: 'skipped', label: '未填寫數' },
  { id: 'invalid', label: '格式無效數' },
  { id: 'completionRate', label: '題目回答率' },
  { id: 'suppressed', label: '小樣本遮蔽' },
  { id: 'suppressionReason', label: '遮蔽原因' },
  { id: 'samplePolicy', label: '樣本匯出規則' },
]

export function buildAnalyticsExportFile({
  format,
  formTitle,
  questionCharts,
  generatedAt = new Date(),
}: BuildAnalyticsExportFileInput): AnalyticsExportFile {
  const fileBase = `${sanitizeFileName(formTitle)}-analytics-${formatTimestamp(generatedAt)}`
  const rows = buildAnalyticsExportRows(questionCharts)

  if (format === 'csv') {
    return {
      format,
      fileName: `${fileBase}.csv`,
      mimeType: 'text/csv;charset=utf-8',
      content: buildCsvContent(rows),
      rowCount: rows.length,
      mockSafe: true,
    }
  }

  if (format === 'excel') {
    return {
      format,
      fileName: `${fileBase}.xls`,
      mimeType: 'application/vnd.ms-excel;charset=utf-8',
      content: buildExcelHtmlTable({
        title: `${formTitle} 圖表統計`,
        rows,
        generatedAt,
      }),
      rowCount: rows.length,
      mockSafe: true,
    }
  }

  return {
    format,
    fileName: `${fileBase}.json`,
    mimeType: 'application/json;charset=utf-8',
    content: JSON.stringify(
      {
        source: 'synthetic_mock',
        exportKind: 'analytics_aggregate',
        format: 'json',
        generatedAt: generatedAt.toISOString(),
        rowCount: rows.length,
        columns: analyticsExportColumns,
        rows,
      },
      null,
      2,
    ),
    rowCount: rows.length,
    mockSafe: true,
  }
}

export function buildAnalyticsExportRows(
  questionCharts: readonly AnalyticsExportChartInput[],
): AnalyticsExportRow[] {
  return questionCharts.flatMap((chart) => {
    const segments: AnalyticsExportChartInput['segments'] =
      chart.segments.length > 0
        ? chart.segments
        : [{ label: '統計摘要', value: chart.totalAnswers, percent: 100 }]
    const suppression = chart.suppression ?? {
      suppressed: false,
      threshold: 0,
      sampleSize: chart.totalAnswers,
    }
    const suppressionReason = suppression.reason ?? ''
    const samplePolicy = resolveSamplePolicy(chart)

    return segments.map((segment) => ({
      questionId: chart.questionId,
      questionTitle: chart.questionTitle,
      questionType: chart.questionType,
      chartKind: chart.chartKind,
      segmentLabel: segment.label,
      segmentValue: segment.value,
      segmentPercent: segment.percent,
      totalAnswers: chart.totalAnswers,
      skipped: chart.skipped,
      invalid: chart.invalid ?? 0,
      completionRate: chart.completionRate,
      suppressed: Boolean(suppression.suppressed || segment.suppressed),
      suppressionReason: suppression.suppressed ? suppressionReason : '',
      samplePolicy,
    }))
  })
}

export function downloadAnalyticsExportFile(file: AnalyticsExportFile) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return false

  const blob = new Blob([file.content], { type: file.mimeType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = file.fileName
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)

  return true
}

function buildCsvContent(rows: readonly AnalyticsExportRow[]) {
  const header = analyticsExportColumns.map(({ id }) => id).join(',')
  const body = rows.map((row) =>
    analyticsExportColumns.map((column) => escapeCsvValue(row[column.id])).join(','),
  )
  return [header, ...body].join('\r\n')
}

function buildExcelHtmlTable({
  title,
  rows,
  generatedAt,
}: {
  title: string
  rows: readonly AnalyticsExportRow[]
  generatedAt: Date
}) {
  const headerCells = analyticsExportColumns
    .map((column) => `<th>${escapeHtml(column.label)}</th>`)
    .join('')
  const bodyRows = rows
    .map(
      (row) =>
        `<tr>${analyticsExportColumns
          .map((column) => `<td>${escapeHtml(protectSpreadsheetFormula(row[column.id]))}</td>`)
          .join('')}</tr>`,
    )
    .join('')

  return `<!doctype html>
<html lang="zh-Hant">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, "Microsoft JhengHei", sans-serif; color: #0f172a; margin: 24px; }
    h1 { font-size: 20px; margin: 0 0 8px; }
    p { color: #475569; font-size: 12px; margin: 0 0 16px; }
    table { border-collapse: collapse; width: 100%; font-size: 12px; }
    th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f1f5f9; color: #334155; font-weight: 700; }
    td { mso-number-format: "\\@"; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p>Mock-safe analytics aggregate export. Generated at ${escapeHtml(generatedAt.toISOString())}. Raw answers, PII samples and backend export jobs are not included.</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`
}

function resolveSamplePolicy(chart: AnalyticsExportChartInput): AnalyticsExportSamplePolicy {
  if (isPiiQuestionType(chart.questionType)) return 'pii_format_status_only'
  if ((chart.samples?.length ?? 0) > 0) return 'samples_not_exported'
  return 'aggregate_only'
}

function isPiiQuestionType(questionType: string) {
  return ['EMAIL', 'PHONE_NUMBER', 'TAIWAN_MOBILE', 'TAIWAN_LANDLINE', 'TAIWAN_ADDRESS'].includes(
    questionType,
  )
}

function escapeCsvValue(value: AnalyticsExportRow[keyof AnalyticsExportRow]) {
  const protectedValue = protectSpreadsheetFormula(value)
  if (!/[",\r\n]/.test(protectedValue)) return protectedValue
  return `"${protectedValue.replaceAll('"', '""')}"`
}

function protectSpreadsheetFormula(value: AnalyticsExportRow[keyof AnalyticsExportRow]) {
  const stringValue = String(value)
  if (/^[=+@]/.test(stringValue)) return `'${stringValue}`
  if (
    (stringValue.startsWith('-') && stringValue.length > 1) ||
    stringValue.startsWith('\t') ||
    stringValue.startsWith('\r')
  ) {
    return `'${stringValue}`
  }
  return stringValue
}

function escapeHtml(value: string | number | boolean) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function sanitizeFileName(value: string) {
  const controlCharacterRange = '\\u0000-\\u001F'
  const invalidFileNamePattern = new RegExp(`[<>:"/\\\\|?*${controlCharacterRange}]+`, 'g')
  const sanitized = value
    .trim()
    .replace(invalidFileNamePattern, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[.\s-]+|[.\s-]+$/g, '')
  return sanitized || 'miaoform'
}

function formatTimestamp(value: Date) {
  const parts = [
    value.getUTCFullYear(),
    String(value.getUTCMonth() + 1).padStart(2, '0'),
    String(value.getUTCDate()).padStart(2, '0'),
    '-',
    String(value.getUTCHours()).padStart(2, '0'),
    String(value.getUTCMinutes()).padStart(2, '0'),
    String(value.getUTCSeconds()).padStart(2, '0'),
  ]
  return parts.join('')
}
